import Styles from './Flipbook.module.css';
import { AssetsClient } from '@shared/frontend';
import { useEffect, useMemo, useState } from 'react';
import type { IAssetDTO } from '../../models/IAssetDTO';

/** Frame data for the flipbook. */
interface IFlipbookFrame {
  url: string;
  width: number;
  height: number;
}

/** Props for the Flipbook component. */
export interface IFlipbookDTO {
  title: string;
  assetDTOs: IAssetDTO[];
  rtl?: boolean;
  intervalMilliseconds?: number;
  fadeMilliseconds?: number;
  classNameContainer?: string;
  classNameStage?: string;
  classNameImage?: string;
  classNameContainerLoading?: string;
  textLoading?: string;
}

/**
 * Renders a crossfading flipbook slideshow from multiple assets.
 *
 * @param props - Component props.
 * @returns The rendered flipbook element.
 */
export function Flipbook(props: IFlipbookDTO) {
  const { title, assetDTOs, rtl, intervalMilliseconds = 3500, fadeMilliseconds = 500, classNameContainer, classNameStage, classNameImage, classNameContainerLoading, textLoading } = props;

  const [frames, setFrames] = useState<IFlipbookFrame[]>([]);
  const [activeIndex, setActiveIndex] = useState(0);

  useEffect(() => {
    let cancelled = false;

    const loadFrames = async () => {
      const nextFrames = await Promise.all(
        assetDTOs.map(async (assetDTO) => {
          const url = await AssetsClient.GetAssetURI(assetDTO.manifest, assetDTO.bundle, assetDTO.asset);
          if (!url) return null;

          const image = new globalThis.Image();
          image.crossOrigin = 'anonymous';

          return await new Promise<IFlipbookFrame | null>((resolve) => {
            image.onload = () => resolve({ url, width: image.naturalWidth || 1, height: image.naturalHeight || 1 });
            image.onerror = () => resolve(null);
            image.src = url;
          });
        })
      );

      if (!cancelled) {
        setFrames(nextFrames.filter((frame): frame is IFlipbookFrame => frame !== null));
        setActiveIndex(0);
      }
    };

    void loadFrames();

    return () => {
      cancelled = true;
    };
  }, [assetDTOs]);

  useEffect(() => {
    if (frames.length < 2) return;
    const timerID = window.setInterval(() => {
      setActiveIndex((current) => (current + 1) % frames.length);
    }, intervalMilliseconds);
    return () => window.clearInterval(timerID);
  }, [frames.length, intervalMilliseconds]);

  const resolvedTitle = useMemo(() => (rtl && title ? `\u200F${title}` : title), [rtl, title]);
  const containerClassName = useMemo(() => (classNameContainer ? `${Styles.container} ${classNameContainer}` : Styles.container), [classNameContainer]);
  const stageClassName = useMemo(() => (classNameStage ? `${Styles.stage} ${classNameStage}` : Styles.stage), [classNameStage]);
  const imageClassName = useMemo(() => (classNameImage ? `${Styles.image} ${classNameImage}` : Styles.image), [classNameImage]);
  const loadingClassName = useMemo(() => (classNameContainerLoading ? `${Styles.containerLoading} ${classNameContainerLoading}` : Styles.containerLoading), [classNameContainerLoading]);
  const aspectRatio = useMemo(() => (frames[0] ? `${frames[0].width} / ${frames[0].height}` : undefined), [frames]);

  if (!frames.length) {
    return (
      <div className={loadingClassName} title={resolvedTitle}>
        {textLoading}
      </div>
    );
  }

  return (
    <div className={containerClassName} title={resolvedTitle} style={aspectRatio ? { aspectRatio } : undefined}>
      <div className={stageClassName}>
        {frames.map((frame, index) => (
          <img
            key={frame.url}
            src={frame.url}
            alt={resolvedTitle}
            title={resolvedTitle}
            crossOrigin="anonymous"
            className={imageClassName}
            style={{ opacity: index === activeIndex ? 1 : 0, transitionDuration: `${fadeMilliseconds}ms` }}
          />
        ))}
      </div>
    </div>
  );
}
